/**
 * @file Application shell layout.
 * @description Provides responsive sidebar/topbar navigation, route links, logout action, and page content container.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Upload,
  Download,
  Eye,
  History,
  Copy,
  Megaphone,
  Building2,
  Share2,
  Settings,
  Menu,
  X,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../utils/cn';

export interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  name: string;
  href: string;
  icon: LucideIcon;
};

const MOBILE_SIDEBAR_MS = 300;

const menuItems: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'View Leads', href: '/leads/view', icon: Eye },
  { name: 'Upload Leads', href: '/leads/upload', icon: Upload },
  { name: 'Export Leads', href: '/leads/export', icon: Download },
  { name: 'Campaigns', href: '/campaigns', icon: Megaphone },
  { name: 'Lead Upload Files', href: '/lead-Upload-Files', icon: History },
  { name: 'Duplicate File', href: '/duplicate-files', icon: Copy },
  { name: 'Source Create', href: '/sources/create', icon: Share2 },
  { name: 'Advertisers', href: '/advertisers', icon: Building2 },
  { name: 'Settings', href: '/settings', icon: Settings },
];

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <Link to="/dashboard" className="group flex items-center gap-3 min-w-0">
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 shadow-md shadow-primary-500/25 transition-transform group-hover:scale-[1.02]',
          compact ? 'h-9 w-9' : 'h-10 w-10',
        )}
      >
        <span className={cn('font-bold text-white', compact ? 'text-base' : 'text-lg')}>J</span>
      </div>
      {!compact && (
        <div className="min-w-0">
          <p className="truncate text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
            JIGGLE
          </p>
          <p className="truncate text-xs font-medium text-gray-500">Lead Management</p>
        </div>
      )}
    </Link>
  );
}

interface SidebarNavProps {
  pathname: string;
  onNavigate?: () => void;
}

function SidebarNav({ pathname, onNavigate }: SidebarNavProps) {
  const isActive = (href: string) => pathname === href;

  return (
    <nav className="sidebar-nav-scroll min-h-0 flex-1 overflow-y-auto px-3 py-4">
      <ul className="space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <li key={item.name}>
              <Link
                to={item.href}
                onClick={onNavigate}
                className={cn(
                  'group flex items-center gap-3 rounded-lg p-1 text-sm font-medium transition-all duration-200',
                  active
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md shadow-primary-500/20'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                    active
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-600',
                  )}
                >
                  <Icon size={18} strokeWidth={active ? 2.25 : 2} />
                </span>
                <span className="truncate">{item.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function SidebarFooter({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="mt-auto shrink-0 border-t border-gray-100 bg-white p-3">
      <button
        type="button"
        onClick={onLogout}
        className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors group-hover:bg-red-100">
          <LogOut size={18} />
        </span>
        <span>Logout</span>
      </button>
    </div>
  );
}

function SidebarPanel({
  onClose,
  onLogout,
  pathname,
}: {
  onClose?: () => void;
  onLogout: () => void;
  pathname: string;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-5">
        <BrandMark />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 lg:hidden"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        )}
      </div>
      <SidebarNav
        pathname={pathname}
        {...(onClose ? { onNavigate: onClose } : {})}
      />
      <SidebarFooter onLogout={onLogout} />
    </div>
  );
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarShown, setSidebarShown] = useState(false);
  const closeSidebarTimeoutRef = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!sidebarOpen) {
      setSidebarShown(false);
      return;
    }
    setSidebarShown(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setSidebarShown(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [sidebarOpen]);

  useEffect(() => {
    return () => {
      if (closeSidebarTimeoutRef.current !== null) {
        window.clearTimeout(closeSidebarTimeoutRef.current);
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('jiggle_token');
    localStorage.removeItem('jiggle_user');
    window.location.href = '/login';
  };

  const openSidebar = () => {
    if (closeSidebarTimeoutRef.current !== null) {
      window.clearTimeout(closeSidebarTimeoutRef.current);
      closeSidebarTimeoutRef.current = null;
    }
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarShown(false);
    closeSidebarTimeoutRef.current = window.setTimeout(() => {
      setSidebarOpen(false);
      closeSidebarTimeoutRef.current = null;
    }, MOBILE_SIDEBAR_MS);
  };

  const handleLogoutFromSidebar = () => {
    closeSidebar();
    handleLogout();
  };

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      {/* Mobile sidebar drawer — slide + fade (UI only) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" aria-hidden={!sidebarShown}>
          <button
            type="button"
            className={cn(
              'fixed inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity duration-300 ease-out',
              sidebarShown ? 'opacity-100' : 'opacity-0',
            )}
            aria-label="Close menu overlay"
            onClick={closeSidebar}
          />
          <aside
            className={cn(
              'fixed inset-y-0 left-0 flex h-full w-[min(100%,20rem)] flex-col border-r border-gray-200 bg-white shadow-2xl',
              'transition-transform duration-300 ease-out will-change-transform',
              sidebarShown ? 'translate-x-0' : '-translate-x-full',
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <SidebarPanel
              pathname={location.pathname}
              onClose={closeSidebar}
              onLogout={handleLogoutFromSidebar}
            />
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:h-screen lg:w-64 lg:flex-col">
        <div className="flex h-full min-h-0 flex-col overflow-hidden border-r border-gray-200 bg-white shadow-sm">
          <SidebarPanel pathname={location.pathname} onLogout={handleLogout} />
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
        {/* Mobile top bar */}
        <header className="sticky top-0 z-10 flex h-16 shrink-0 items-center border-b border-gray-200 bg-white/90 backdrop-blur-md lg:hidden">
          <button
            type="button"
            className="flex h-16 w-14 items-center justify-center border-r border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={openSidebar}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <div className="flex flex-1 items-center justify-center px-4">
            <BrandMark compact />
            <span className="ml-2 truncate text-lg font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              JIGGLE
            </span>
          </div>
          <div className="w-14" aria-hidden />
        </header>

        <main className="relative flex-1 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
